import type React from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { ReactComponent as GenericFileIcon } from 'new/components/icons/fileOutlined.svg';

/** A file-type mapping supplied by an icon adapter. */
export interface IconMapping {
  extensions: string[];
  /** Exact, case-insensitive whole-filename matches (e.g. 'dockerfile') for files with no real extension. */
  filenames?: string[];
  mimeTypes?: string[];
  icon: React.ComponentType<SvgIconProps>;
  category?: string;
}

/** Resolves file names and MIME types to icon components. */
export interface FileIconResolver {
  resolveIcon(fileName: string, mimeType?: string): React.ComponentType<SvgIconProps>;
  registerMapping(mapping: IconMapping): void;
}

/** Configuration used when creating a file icon resolver. */
export interface ResolverConfig {
  adapter?: () => IconMapping[];
  cacheEnabled?: boolean;
  fallbackIcon?: React.ComponentType<SvgIconProps>;
}

type IconComponent = React.ComponentType<SvgIconProps>;

/** SVGR components aren't typed as MUI icons; this cast bridges the two at a single point. */
export const asIconComponent = (svg: unknown): IconComponent => svg as unknown as IconComponent;

const defaultFallbackIcon = asIconComponent(GenericFileIcon);
const normalizeMimeType = (mimeType?: string) => mimeType?.split(';')[0].trim().toLowerCase();

const getBaseName = (fileName: string) => (fileName.split(/[\\/]/).pop() ?? '').toLowerCase();

const getExtension = (baseName: string) => {
  const lastDot = baseName.lastIndexOf('.');
  return lastDot >= 0 && lastDot < baseName.length - 1 ? baseName.slice(lastDot + 1) : '';
};

const findByFilename = (mappings: IconMapping[], baseName: string): IconComponent | undefined =>
  mappings.find((mapping) =>
    mapping.filenames?.some((candidate) => candidate.toLowerCase() === baseName),
  )?.icon;

const findByExtension = (mappings: IconMapping[], extension: string): IconComponent | undefined =>
  mappings.find((mapping) =>
    mapping.extensions.some((candidate) => candidate.toLowerCase() === extension),
  )?.icon;

const findByMimeType = (
  mappings: IconMapping[],
  normalizedMimeType?: string,
): IconComponent | undefined =>
  mappings.find((mapping) =>
    mapping.mimeTypes?.some((candidate) => candidate.toLowerCase() === normalizedMimeType),
  )?.icon;

const findByCategory = (
  mappings: IconMapping[],
  normalizedMimeType?: string,
): IconComponent | undefined =>
  mappings.find(
    (mapping) => mapping.category != null && mapping.category === normalizedMimeType?.split('/')[0],
  )?.icon;

/** Creates an extensible resolver with filename, extension, MIME, category, and fallback precedence. */
export const createFileIconResolver = (config: ResolverConfig = {}): FileIconResolver => {
  const mappings = [...(config.adapter?.() ?? [])];
  const cache = new Map<string, IconComponent>();
  const fallbackIcon = config.fallbackIcon ?? defaultFallbackIcon;
  const cacheEnabled = config.cacheEnabled ?? true;

  const resolveIcon = (fileName: string, mimeType?: string): IconComponent => {
    const baseName = getBaseName(fileName);
    const extension = getExtension(baseName);
    const normalizedMimeType = normalizeMimeType(mimeType);
    // Extensionless filenames (e.g. "Dockerfile") all share extension === '', so the cache key
    // falls back to the full base name for them to avoid unrelated names colliding on one entry.
    const cacheKey = `${extension || baseName}|${normalizedMimeType ?? ''}`;
    const cachedIcon = cacheEnabled ? cache.get(cacheKey) : undefined;
    if (cachedIcon) return cachedIcon;

    const icon =
      findByFilename(mappings, baseName) ??
      findByExtension(mappings, extension) ??
      findByMimeType(mappings, normalizedMimeType) ??
      findByCategory(mappings, normalizedMimeType) ??
      fallbackIcon;

    if (cacheEnabled) cache.set(cacheKey, icon);
    return icon;
  };

  return {
    resolveIcon,
    registerMapping: (mapping) => {
      mappings.push(mapping);
      cache.clear();
    },
  };
};
